import { useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useOnboardingSteps } from '../../hooks/useOnboardingSteps';
import type {
  OnboardingStep,
  OnboardingBranch,
  CreateOnboardingStepInput,
  UpdateOnboardingStepInput,
} from '../../services/api/onboardingSteps';
import { Spinner } from '../../components/common/Spinner';

// ---- Branch display helpers ----

const BRANCH_LABELS: Record<OnboardingBranch, string> = {
  steps: 'Onboarding Steps',
  admin_employee: 'Admin — Employee',
  admin_contractor: 'Admin — Contractor',
};

const BRANCHES: OnboardingBranch[] = ['steps', 'admin_employee', 'admin_contractor'];

// ---- Form state ----

interface FormValues {
  step_id: string;
  branch: OnboardingBranch;
  label: string;
  body_markdown: string;
  links: { label: string; url: string }[];
  flags: string[];
  active: boolean;
}

const EMPTY_FORM: FormValues = {
  step_id: '',
  branch: 'steps',
  label: '',
  body_markdown: '',
  links: [],
  flags: [],
  active: true,
};

function stepToFormValues(step: OnboardingStep): FormValues {
  return {
    step_id: step.step_id,
    branch: step.branch,
    label: step.label,
    body_markdown: step.body_markdown,
    links: step.links.map((l) => ({ label: l.label, url: l.url })),
    flags: [...step.flags],
    active: step.active,
  };
}

// ---- Sortable row ----

interface SortableStepRowProps {
  step: OnboardingStep;
  onEdit: (step: OnboardingStep) => void;
  onDelete: (id: number) => void;
  onRestore: (id: number) => void;
  isDeleting: boolean;
  isRestoring: boolean;
}

function SortableStepRow({
  step,
  onEdit,
  onDelete,
  onRestore,
  isDeleting,
  isRestoring,
}: SortableStepRowProps) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: step.id,
    disabled: !step.active,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 rounded border px-3 py-2 ${
        step.active ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
      }`}
    >
      {step.active && (
        <button
          type="button"
          aria-label="Drag to reorder"
          className="mt-0.5 cursor-grab text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
        >
          <Bars3Icon className="h-4 w-4" />
        </button>
      )}
      {!step.active && <div className="mt-0.5 h-4 w-4 flex-shrink-0" />}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{step.label}</span>
          <span className="font-mono text-xs text-gray-400">{step.step_id}</span>
          {!step.active && (
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-500">
              Inactive
            </span>
          )}
          {step.flags.length > 0 &&
            step.flags.map((f) => (
              <span
                key={f}
                className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700"
              >
                {f}
              </span>
            ))}
        </div>
        {step.body_markdown && (
          <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">{step.body_markdown}</p>
        )}
        {step.links.length > 0 && (
          <p className="mt-0.5 text-xs text-gray-400">
            {step.links.length} link{step.links.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center gap-1">
        {step.active ? (
          <>
            <button
              type="button"
              aria-label={`Edit ${step.label}`}
              onClick={() => onEdit(step)}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={`Delete ${step.label}`}
              onClick={() => onDelete(step.id)}
              disabled={isDeleting}
              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button
            type="button"
            aria-label={`Restore ${step.label}`}
            onClick={() => onRestore(step.id)}
            disabled={isRestoring}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 disabled:opacity-40"
          >
            <ArrowPathIcon className="h-3 w-3" />
            Restore
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Step form (create / edit) ----

interface StepFormProps {
  initial: FormValues;
  isEdit: boolean;
  isSaving: boolean;
  error: string | null;
  onSave: (values: FormValues) => void;
  onCancel: () => void;
}

function StepForm({ initial, isEdit, isSaving, error, onSave, onCancel }: StepFormProps) {
  const [values, setValues] = useState<FormValues>(initial);
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newFlag, setNewFlag] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  function setField<K extends keyof FormValues>(key: K, val: FormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  function addLink() {
    if (!newLinkLabel.trim() || !newLinkUrl.trim()) return;
    setField('links', [...values.links, { label: newLinkLabel.trim(), url: newLinkUrl.trim() }]);
    setNewLinkLabel('');
    setNewLinkUrl('');
  }

  function removeLink(idx: number) {
    setField(
      'links',
      values.links.filter((_, i) => i !== idx),
    );
  }

  function addFlag() {
    const f = newFlag.trim();
    if (!f || values.flags.includes(f)) return;
    setField('flags', [...values.flags, f]);
    setNewFlag('');
  }

  function removeFlag(f: string) {
    setField(
      'flags',
      values.flags.filter((x) => x !== f),
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h3 className="mb-4 text-base font-medium text-gray-900">
        {isEdit ? 'Edit step' : 'Create step'}
      </h3>

      {error && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {/* step_id + branch — locked after create */}
        {!isEdit && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Step ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={values.step_id}
                onChange={(e) => setField('step_id', e.target.value)}
                placeholder="e.g. gmail"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Branch <span className="text-red-500">*</span>
              </label>
              <select
                value={values.branch}
                onChange={(e) => setField('branch', e.target.value as OnboardingBranch)}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              >
                {BRANCHES.map((b) => (
                  <option key={b} value={b}>
                    {BRANCH_LABELS[b]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* label */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">
            Label <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={values.label}
            onChange={(e) => setField('label', e.target.value)}
            placeholder="e.g. 📧 Gmail"
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>

        {/* body_markdown */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs font-medium text-gray-700">Body (Markdown)</label>
            <button
              type="button"
              onClick={() => setShowPreview((p) => !p)}
              className="text-xs text-blue-600 hover:underline"
            >
              {showPreview ? 'Edit' : 'Preview'}
            </button>
          </div>
          {showPreview ? (
            <pre className="min-h-[6rem] w-full whitespace-pre-wrap rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
              {values.body_markdown || <span className="text-gray-400 italic">No content</span>}
            </pre>
          ) : (
            <textarea
              value={values.body_markdown}
              onChange={(e) => setField('body_markdown', e.target.value)}
              rows={5}
              placeholder="Markdown content…"
              className="w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-sm"
            />
          )}
        </div>

        {/* links editor */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Links</label>
          <div className="space-y-1.5">
            {values.links.map((link, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate text-gray-700">
                  {link.label} — <span className="text-blue-600">{link.url}</span>
                </span>
                <button
                  type="button"
                  aria-label={`Remove link ${link.label}`}
                  onClick={() => removeLink(idx)}
                  className="text-gray-400 hover:text-red-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <input
                type="text"
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
                placeholder="Label"
                className="w-1/3 rounded border border-gray-300 px-2 py-1 text-xs"
              />
              <input
                type="url"
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                placeholder="https://…"
                className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addLink();
                  }
                }}
              />
              <button
                type="button"
                onClick={addLink}
                className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* flags editor */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Flags</label>
          <div className="flex flex-wrap gap-1.5">
            {values.flags.map((f) => (
              <span
                key={f}
                className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700"
              >
                {f}
                <button
                  type="button"
                  aria-label={`Remove flag ${f}`}
                  onClick={() => removeFlag(f)}
                  className="ml-0.5 text-blue-400 hover:text-blue-600"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
            <div className="flex gap-1">
              <input
                type="text"
                value={newFlag}
                onChange={(e) => setNewFlag(e.target.value)}
                placeholder="flag_name"
                className="rounded border border-gray-300 px-2 py-0.5 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addFlag();
                  }
                }}
              />
              <button
                type="button"
                onClick={addFlag}
                className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-100"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* active toggle (edit only) */}
        {isEdit && (
          <div className="flex items-center gap-2">
            <input
              id="active-toggle"
              type="checkbox"
              checked={values.active}
              onChange={(e) => setField('active', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <label htmlFor="active-toggle" className="text-sm text-gray-700">
              Active
            </label>
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={() => onSave(values)}
          disabled={isSaving}
          className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create step'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- Branch section ----

interface BranchSectionProps {
  branch: OnboardingBranch;
  steps: OnboardingStep[];
  onEdit: (step: OnboardingStep) => void;
  onDelete: (id: number) => void;
  onRestore: (id: number) => void;
  onReorder: (branch: OnboardingBranch, stepIds: number[]) => void;
  isDeleting: boolean;
  isRestoring: boolean;
}

function BranchSection({
  branch,
  steps,
  onEdit,
  onDelete,
  onRestore,
  onReorder,
  isDeleting,
  isRestoring,
}: BranchSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const activeSteps = steps.filter((s) => s.active);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = activeSteps.findIndex((s) => s.id === active.id);
    const newIdx = activeSteps.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(activeSteps, oldIdx, newIdx);
    onReorder(branch, reordered.map((s) => s.id));
  }

  return (
    <div>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext
          items={activeSteps.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-1.5">
            {steps.map((step) => (
              <SortableStepRow
                key={step.id}
                step={step}
                onEdit={onEdit}
                onDelete={onDelete}
                onRestore={onRestore}
                isDeleting={isDeleting}
                isRestoring={isRestoring}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {steps.length === 0 && (
        <p className="py-4 text-sm italic text-gray-400">No steps in this branch.</p>
      )}
    </div>
  );
}

// ---- Page ----

export default function OnboardingStepsPage() {
  const {
    data: grouped,
    isLoading,
    error: queryError,
    createMutation,
    updateMutation,
    reorderMutation,
    softDeleteMutation,
    restoreMutation,
  } = useOnboardingSteps();

  const [formMode, setFormMode] = useState<'hidden' | 'create' | 'edit'>('hidden');
  const [editingStep, setEditingStep] = useState<OnboardingStep | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  function openCreate() {
    setEditingStep(null);
    setFormError(null);
    setFormMode('create');
  }

  function openEdit(step: OnboardingStep) {
    setEditingStep(step);
    setFormError(null);
    setFormMode('edit');
  }

  function closeForm() {
    setFormMode('hidden');
    setEditingStep(null);
    setFormError(null);
  }

  async function handleSave(values: FormValues) {
    setFormError(null);
    try {
      if (formMode === 'create') {
        const input: CreateOnboardingStepInput = {
          step_id: values.step_id.trim(),
          branch: values.branch,
          label: values.label.trim(),
          body_markdown: values.body_markdown,
          links: values.links,
          flags: values.flags,
        };
        await createMutation.mutateAsync(input);
      } else if (formMode === 'edit' && editingStep) {
        const input: UpdateOnboardingStepInput = {
          label: values.label.trim(),
          body_markdown: values.body_markdown,
          links: values.links,
          flags: values.flags,
          active: values.active,
        };
        await updateMutation.mutateAsync({ id: editingStep.id, input });
      }
      closeForm();
    } catch (err) {
      setFormError((err as Error).message ?? 'An error occurred');
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Soft-delete this step? It can be restored later.')) return;
    try {
      await softDeleteMutation.mutateAsync(id);
    } catch (err) {
      alert((err as Error).message ?? 'Failed to delete step');
    }
  }

  async function handleRestore(id: number) {
    try {
      await restoreMutation.mutateAsync(id);
    } catch (err) {
      alert((err as Error).message ?? 'Failed to restore step');
    }
  }

  function handleReorder(branch: OnboardingBranch, stepIds: number[]) {
    reorderMutation.mutate({ branch, stepIds });
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        Failed to load onboarding steps: {(queryError as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Onboarding Step Catalog</h1>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4" />
          New step
        </button>
      </div>

      {/* Create / edit form */}
      {formMode !== 'hidden' && (
        <StepForm
          initial={formMode === 'edit' && editingStep ? stepToFormValues(editingStep) : EMPTY_FORM}
          isEdit={formMode === 'edit'}
          isSaving={isSaving}
          error={formError}
          onSave={handleSave}
          onCancel={closeForm}
        />
      )}

      {/* Branch sections */}
      {BRANCHES.map((branch) => {
        const steps = grouped?.[branch] ?? [];
        return (
          <section key={branch}>
            <h2 className="mb-2 text-base font-semibold text-gray-800">{BRANCH_LABELS[branch]}</h2>
            <BranchSection
              branch={branch}
              steps={steps}
              onEdit={openEdit}
              onDelete={handleDelete}
              onRestore={handleRestore}
              onReorder={handleReorder}
              isDeleting={softDeleteMutation.isPending}
              isRestoring={restoreMutation.isPending}
            />
          </section>
        );
      })}
    </div>
  );
}
